import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, PenLine, Loader2, FileDown, Check, Flame } from "lucide-react";
import { jsPDF } from "jspdf";
import { streamPost, API } from "@/lib/stream";

const DEPARTMENTS = ["Operations", "Leasing", "Maintenance", "Corporate", "Regional"];
const PRIORITIES = [
  { value: "standard", label: "Standard", cls: "border-slate-900/10 text-slate-600" },
  { value: "high", label: "High", cls: "border-slate-900/10 text-slate-600" },
  { value: "critical", label: "Critical", cls: "border-slate-900/10 text-slate-600" },
];
const PRIORITY_BADGE = {
  standard: "bg-slate-100 text-slate-600",
  high: "bg-amber-100 text-[#B45309]",
  critical: "bg-red-100 text-red-700",
};

const inputCls =
  "w-full bg-white rounded-2xl border-2 border-slate-900/10 px-4 py-3 text-sm font-medium outline-none focus:border-[#FF5722] transition-colors placeholder:text-slate-400";

const fmtUsd = (n) => (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const timeAgo = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function RequisitionSection() {
  const [form, setForm] = useState({ role: "", department: "Operations", hiring_manager: "", openings: 1, salary_min: 45000, salary_max: 65000, priority: "standard", justification: "" });
  const [reqs, setReqs] = useState([]);
  const [saved, setSaved] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [jd, setJd] = useState("");
  const [jdBusy, setJdBusy] = useState(false);
  const [applyFor, setApplyFor] = useState(null);
  const [appForm, setAppForm] = useState({ name: "", email: "", note: "" });
  const [resume, setResume] = useState(null);
  const [appState, setAppState] = useState("idle");

  const readFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const submitApplication = async (reqId) => {
    if (appState === "sending" || !appForm.name.trim() || !appForm.email.trim()) return;
    setAppState("sending");
    try {
      let resume_b64 = "";
      if (resume) resume_b64 = await readFile(resume);
      const res = await fetch(`${API}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ req_id: reqId, ...appForm, resume_filename: resume?.name || "", resume_b64 }),
      });
      if (res.ok) {
        setAppState("sent");
        setAppForm({ name: "", email: "", note: "" });
        setResume(null);
        await load();
        setTimeout(() => { setAppState("idle"); setApplyFor(null); }, 4000);
      } else {
        const d = await res.json();
        setAppState(d.detail || "error");
        setTimeout(() => setAppState("idle"), 4000);
      }
    } catch {
      setAppState("error");
      setTimeout(() => setAppState("idle"), 4000);
    }
  };

  const load = async () => {
    try {
      const res = await fetch(`${API}/requisitions`);
      const data = await res.json();
      setReqs(data.requisitions || []);
    } catch { /* board stays as-is */ }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (saveBusy || !form.role.trim()) return;
    setSaveBusy(true);
    try {
      const res = await fetch(`${API}/requisitions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSaved(true);
        await load();
        setTimeout(() => setSaved(false), 3500);
      }
    } catch { /* ignore */ }
    setSaveBusy(false);
  };

  const draftJd = async () => {
    if (jdBusy) return;
    setJdBusy(true);
    setJd("");
    try {
      await streamPost(
        "/draft",
        {
          doc_type: "job-description",
          role: form.role,
          company: form.department + " team",
          tone: "Warm & professional",
          extras: `Hiring manager: ${form.hiring_manager || "TBD"}. Openings: ${form.openings}. Salary band: ${fmtUsd(form.salary_min)}-${fmtUsd(form.salary_max)}. Priority: ${form.priority}. Justification: ${form.justification || "team growth"}`,
        },
        (d) => setJd((o) => o + d)
      );
    } catch {
      setJd("Job description failed to generate — please try again.");
    }
    setJdBusy(false);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = 612;
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, W, 96, "F");
    doc.setFillColor(255, 87, 34);
    doc.rect(0, 96, W, 6, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("Hiring Requisition", 48, 44);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(203, 213, 225);
    doc.text(`Opera360 AI  ·  Recruiting  ·  ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`, 48, 66);
    let y = 140;
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "bold");
    doc.text("REQUISITION DETAILS", 48, y);
    y += 22;
    doc.setFont("helvetica", "normal");
    [
      ["Role", form.role || "—"],
      ["Department", form.department],
      ["Hiring manager", form.hiring_manager || "TBD"],
      ["Openings", String(form.openings)],
      ["Salary band", `${fmtUsd(form.salary_min)} – ${fmtUsd(form.salary_max)}`],
      ["Priority", form.priority.toUpperCase()],
    ].forEach(([k, v]) => {
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(12);
      doc.text(k, 48, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 87, 34);
      doc.text(String(v), W - 48, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setDrawColor(226, 232, 240);
      doc.line(48, y + 10, W - 48, y + 10);
      y += 34;
    });
    if (form.justification) {
      y += 10;
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("JUSTIFICATION", 48, y);
      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(51, 65, 85);
      doc.setFontSize(10.5);
      doc.splitTextToSize(form.justification, W - 96).forEach((ln) => {
        doc.text(ln, 48, y);
        y += 15;
      });
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(148, 163, 184);
    doc.text("Drafted with Opera360 AI — route for approval before posting.", 48, 780);
    doc.save(`requisition-${(form.role || "role").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`);
  };

  return (
    <section id="recruiting" data-testid="recruiting-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF5722]">Recruiting · Requisitions</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mt-3">
            Open a req. Get the job description.
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mt-4">
            File a hiring requisition in seconds — it lands on the live board and in the admin
            pipeline — then let Claude draft the job description from it.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="grid lg:grid-cols-[0.95fr_1.05fr] gap-6 mt-12"
        >
          <div className="rounded-3xl border-2 border-slate-900/5 bg-white p-7 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)] space-y-4">
            <div className="font-display font-bold text-lg flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#FF5722]" /> New requisition</div>
            <input data-testid="req-role-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role, e.g. Maintenance Supervisor" className={inputCls} />
            <div className="grid grid-cols-2 gap-4">
              <select data-testid="req-department-select" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className={inputCls}>
                {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
              </select>
              <input data-testid="req-manager-input" value={form.hiring_manager} onChange={(e) => setForm({ ...form, hiring_manager: e.target.value })} placeholder="Hiring manager" className={inputCls} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Openings</label>
                <input data-testid="req-openings-input" type="number" min="1" value={form.openings} onChange={(e) => setForm({ ...form, openings: Number(e.target.value) || 1 })} className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Salary min</label>
                <input data-testid="req-salary-min-input" type="number" step="1000" value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: Number(e.target.value) || 0 })} className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Salary max</label>
                <input data-testid="req-salary-max-input" type="number" step="1000" value={form.salary_max} onChange={(e) => setForm({ ...form, salary_max: Number(e.target.value) || 0 })} className={`${inputCls} mt-1.5`} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {PRIORITIES.map((p, i) => (
                <button
                  key={p.value}
                  data-testid={`req-priority-${i}`}
                  type="button"
                  onClick={() => setForm({ ...form, priority: p.value })}
                  className={`px-4 py-2 rounded-full text-xs font-bold border-2 transition-colors duration-200 ${
                    form.priority === p.value ? "bg-[#FF5722] text-white border-[#FF5722]" : `${p.cls} hover:border-[#FF5722] hover:text-[#FF5722]`
                  }`}
                >
                  {p.value === "critical" && <Flame className="w-3 h-3 inline mr-1" />}{p.label}
                </button>
              ))}
            </div>
            <textarea data-testid="req-justification-input" value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} rows={3} placeholder="Why this hire, why now…" className={`${inputCls} resize-none`} />
            <div className="flex flex-wrap gap-2.5">
              <button data-testid="req-save-button" onClick={save} disabled={saveBusy || !form.role.trim()} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#FF5722] text-white font-bold text-sm hover:bg-[#0F172A] transition-colors duration-300 disabled:opacity-50">
                {saveBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Briefcase className="w-4 h-4" />}
                {saved ? "On the board" : "Save requisition"}
              </button>
              <button data-testid="req-jd-button" onClick={draftJd} disabled={jdBusy} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#8B5CF6] text-white font-bold text-sm hover:bg-[#0F172A] transition-colors duration-300 disabled:opacity-50">
                {jdBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
                {jdBusy ? "Claude drafting…" : "Draft job description"}
              </button>
            </div>
            {saved && <div data-testid="req-save-success" className="text-xs font-bold text-[#10B981] text-center">Requisition saved — it's live on the board and in the admin pipeline.</div>}
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0F172A] p-6 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.4)] min-h-[16rem] flex flex-col">
              <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-3">
                <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Job description preview</span>
                <button
                  data-testid="req-pdf-button"
                  onClick={exportPdf}
                  disabled={!form.role.trim()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#FF5722] text-xs font-bold text-white hover:bg-[#10B981] transition-colors disabled:opacity-40"
                >
                  <FileDown className="w-3.5 h-3.5" /> Req PDF
                </button>
              </div>
              <div data-testid="req-jd-output" className="doc-preview doc-scroll max-h-64 overflow-y-auto text-xs leading-relaxed text-slate-300 flex-1">
                {jd || <span className="text-slate-500">Fill in the requisition, then let Claude draft the job description here…</span>}
                {jdBusy && <span className="inline-block w-2 h-3.5 bg-[#8B5CF6] animate-pulse ml-0.5 align-middle" />}
              </div>
            </div>

            <div data-testid="req-board" className="rounded-3xl border-2 border-slate-900/5 bg-white p-6 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)]">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-base">Open requisitions board</div>
                <span className="text-xs font-bold text-slate-400">{reqs.length} open</span>
              </div>
              <div className="mt-4 space-y-2.5 max-h-56 overflow-y-auto chat-scroll pr-1">
                <AnimatePresence>
                  {reqs.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No open requisitions yet — file the first one.</div>}
                  {reqs.map((r, i) => (
                    <motion.div
                      key={r.id}
                      data-testid={`req-board-item-${i}`}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4 }}
                      className="rounded-2xl bg-[#F4F4F0] px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold text-slate-800 truncate">{r.role}</div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{r.department} · {r.openings} opening{r.openings > 1 ? "s" : ""} · {r.applicants || 0} applicant{(r.applicants || 0) !== 1 ? "s" : ""} · {timeAgo(r.created_at)}</div>
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${PRIORITY_BADGE[r.priority] || PRIORITY_BADGE.standard}`}>{r.priority}</span>
                        {r.status === "approved" && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-blue-100 text-[#2563EB]">Approved</span>
                        )}
                        <button
                          data-testid={`req-apply-button-${i}`}
                          onClick={() => { setApplyFor(applyFor === r.id ? null : r.id); setAppState("idle"); }}
                          className="px-3.5 py-1.5 rounded-full bg-[#FF5722] text-white text-[11px] font-bold hover:bg-[#0F172A] transition-colors shrink-0"
                        >
                          Apply
                        </button>
                      </div>
                      <AnimatePresence>
                        {applyFor === r.id && (
                          <motion.div
                            data-testid="req-apply-form"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            {appState === "sent" ? (
                              <div data-testid="req-apply-success" className="mt-3 flex items-center gap-2 text-xs font-bold text-[#10B981]">
                                <Check className="w-4 h-4" /> Application sent — the hiring team has it.
                              </div>
                            ) : (
                              <div className="mt-3 space-y-2.5">
                                <div className="grid grid-cols-2 gap-2.5">
                                  <input data-testid="req-apply-name" value={appForm.name} onChange={(e) => setAppForm({ ...appForm, name: e.target.value })} placeholder="Full name" className={inputCls} />
                                  <input data-testid="req-apply-email" type="email" value={appForm.email} onChange={(e) => setAppForm({ ...appForm, email: e.target.value })} placeholder="Email" className={inputCls} />
                                </div>
                                <textarea data-testid="req-apply-note" value={appForm.note} onChange={(e) => setAppForm({ ...appForm, note: e.target.value })} rows={2} placeholder="Why you're a fit (optional)" className={`${inputCls} resize-none`} />
                                <div className="flex flex-wrap items-center gap-2.5">
                                  <label className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border-2 border-dashed border-slate-300 text-[11px] font-bold text-slate-500 cursor-pointer hover:border-[#FF5722] hover:text-[#FF5722] transition-colors">
                                    <FileDown className="w-3.5 h-3.5 rotate-180" />
                                    {resume ? resume.name : "Attach resume (PDF/DOC, 5 MB)"}
                                    <input data-testid="req-apply-resume" type="file" accept=".pdf,.txt,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files?.[0] || null)} />
                                  </label>
                                  <button
                                    data-testid="req-apply-submit"
                                    onClick={() => submitApplication(r.id)}
                                    disabled={appState === "sending" || !appForm.name.trim() || !appForm.email.trim()}
                                    className="px-4 py-2 rounded-full bg-[#0F172A] text-white text-[11px] font-bold hover:bg-[#FF5722] transition-colors disabled:opacity-50"
                                  >
                                    {appState === "sending" ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Submit application"}
                                  </button>
                                </div>
                                {appState !== "idle" && appState !== "sending" && (
                                  <div data-testid="req-apply-error" className="text-[11px] font-bold text-red-600">{appState === "error" ? "Something went wrong — try again." : appState}</div>
                                )}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
